import { productClick, productImpression } from 'analytics/enhanced-ecommerce'
import { GAImpressionFieldObject, productClickGenericObject, productImpressionGenericObject } from 'analytics/eventDefinitions'
import * as Analytics from 'analytics/analytics'
import { clientEvent } from 'analytics/snowplow/events'
import FormatCurrency from 'components/Common/FormatCurrency'
import Image from 'components/Common/Image'
import StickyPromptCard from 'components/Common/StickyPromptCard/StickyPromptCard'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import Pill from 'components/Luxkit/Pill'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { useAppSelector } from 'hooks/reduxHooks'
import uuidV4 from 'lib/uuidV4'
import { rem } from 'polished'
import React, { useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useInView } from 'react-intersection-observer'

const RIGHT_COLUMN_SIZE = 68
// mobile is downsized by 20% so we increase it to compensate
const RIGHT_COLUMN_SIZE_MOBILE = Math.round(RIGHT_COLUMN_SIZE / 0.8)

// the image and button should be of the same width so they are aligned vertically to the right
const ImageBlock = styled.div`
  flex-shrink: 0;

  // mobile
  width: ${rem(RIGHT_COLUMN_SIZE_MOBILE)};
  height: ${rem(RIGHT_COLUMN_SIZE_MOBILE)};
  // desktop
  ${mediaQueryUp.sm} {
    width: ${rem(RIGHT_COLUMN_SIZE)};
    height: ${rem(RIGHT_COLUMN_SIZE)};
  }
`

const BookBlock = styled.div`
  flex-shrink: 0;

  // mobile
  width: ${rem(RIGHT_COLUMN_SIZE_MOBILE)};
  // desktop
  ${mediaQueryUp.sm} {
    width: ${rem(RIGHT_COLUMN_SIZE)};
  }
`

export interface StickyCardViewModel{
  show: boolean,
  offerDisplayName: string,
  durationText: string,
  checkInOutText?: string,
  occupancyText?: string,
  image: App.Image,
  price: number,
  discountPercent: number,
  offerId: string,
  offerAnalyticsType: string,
  analyticsListName: string,
  analyticsCardType: string,
  cardHeading: string,
  toUrl: string,
  onClick: () => void,
  onClose: () => void,
  stickyTop?: number,
}

interface Props extends StickyCardViewModel {
}

function ParameterizedStickyCard({
  show,
  offerDisplayName,
  durationText,
  checkInOutText,
  occupancyText,
  image,
  price,
  discountPercent,
  offerId,
  offerAnalyticsType,
  analyticsListName,
  analyticsCardType,
  cardHeading,
  toUrl,
  onClick,
  onClose,
  stickyTop,
}: Props) {
  // analytics
  const currentCurrency = useAppSelector((state) => { return state.geo.currentCurrency })
  const listId = useMemo(() => uuidV4(), [])

  const [inViewRef, inView] = useInView({ triggerOnce: true })

  const onImpression = useCallback(() => {
    const impression: GAImpressionFieldObject = productImpressionGenericObject({
      id: offerId,
      name: offerDisplayName,
      category: offerAnalyticsType,
      price,
      position: 1,
      listName: analyticsListName,
    })
    productImpression(currentCurrency, [impression], {
      listId,
      listName: analyticsListName,
    })
  }, [listId, offerId, offerDisplayName, offerAnalyticsType, currentCurrency, price, analyticsListName])

  const onClickThrough = useCallback(() => {
    const clickObject = productClickGenericObject(
      {
        id: offerId,
        name: offerDisplayName,
        category: offerAnalyticsType,
        price,
        position: 1,
      },
    )
    productClick(currentCurrency, clickObject, {
      listId,
      listName: analyticsListName,
    })
  }, [listId, offerId, offerDisplayName, offerAnalyticsType, currentCurrency, price, analyticsListName])

  // interaction handlers
  const handleDismiss = useCallback(() => {
    onClose()
    Analytics.track('client-event', clientEvent(listId, `${analyticsCardType}_dismissed`, 'lere', 'interaction'))
  }, [listId, analyticsCardType, onClose])

  const handleClick = useCallback(() => {
    onClick()
    onClickThrough()
  }, [onClick, onClickThrough])

  useEffect(() => {
    if (show && inView) {
      onImpression()
    }
  }, [show, onImpression, inView])

  return (
    <StickyPromptCard to={toUrl} show={show} onClose={handleDismiss} onClick={handleClick} stickyTop={stickyTop} ref={inViewRef}>
      <StickyPromptCard.Header>
        <Heading variant="heading6">{cardHeading}</Heading>
      </StickyPromptCard.Header>
      <StickyPromptCard.Content>
        <Group direction="horizontal" gap={12} fullWidth horizontalAlign="space-between">
          <Group direction="vertical" gap={4}>
            <BodyText weight="bold" variant="medium" colour="neutral-two" lineClamp={1}>{offerDisplayName}</BodyText>
            {
              (!!checkInOutText || !!occupancyText) &&
                <Caption variant="large" >
                  {[checkInOutText, occupancyText].filter(Boolean).join(', ')}
                </Caption>
            }
          </Group>
          <ImageBlock>
            <Image
                id={image.id}
                alt={image.title ?? ''}
                width={RIGHT_COLUMN_SIZE}
                height={RIGHT_COLUMN_SIZE}
                dpr={1}
                fit="center"
              />
          </ImageBlock>
        </Group>
      </StickyPromptCard.Content>
      <StickyPromptCard.Footer>
        <Group direction="horizontal" fullWidth gap={12} horizontalAlign="space-between" verticalAlign="end">
          <Group direction="vertical" gap={4} >
            <Caption variant="medium">{durationText}</Caption>
            <Group direction="horizontal" gap={4}>
              <BodyText variant="large" weight="bold">
                <FormatCurrency value={price} format="roundedDollar" />
              </BodyText>
              {discountPercent > MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE && (
                <Pill kind="primary" variant="success">Save up to {discountPercent}%</Pill>
              )}
            </Group>
          </Group>
          <BookBlock>
            <TextButton kind="primary" size="small" fit="full-width">Book now</TextButton>
          </BookBlock>
        </Group>
      </StickyPromptCard.Footer>
    </StickyPromptCard>
  )
}

export default ParameterizedStickyCard
